(function () {
  'use strict';

  var FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');

  var activeModal = null;
  var activeShadowRoot = null;
  var previouslyFocused = null;
  var watchedHosts = [];

  // Captures the focused element RIGHT BEFORE any pointer interaction — before
  // web components move focus into a newly-opened modal. Without this, activate()
  // would capture an element already inside the modal (wrong restore target).
  var preclickFocused = null;
  document.addEventListener('pointerdown', function () {
    if (!activeModal) {
      preclickFocused = document.activeElement || document.body;
    }
  }, true);

  function getFocusable(root) {
    return Array.from(root.querySelectorAll(FOCUSABLE_SELECTORS)).filter(function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0;
    });
  }

  // composedPath() pierces shadow boundaries — gives the real event target
  function onTabCapture(e) {
    if (!activeModal || e.key !== 'Tab') return;

    var path = e.composedPath ? e.composedPath() : [];
    var currentEl = path[0]
      || (activeShadowRoot && activeShadowRoot.activeElement)
      || document.activeElement;

    var focusable = getFocusable(activeModal);
    e.preventDefault();

    if (!focusable.length) {
      activeModal.focus();
      return;
    }

    var current = focusable.indexOf(currentEl);
    var next;
    if (e.shiftKey) {
      next = current <= 0 ? focusable.length - 1 : current - 1;
    } else {
      next = current >= focusable.length - 1 ? 0 : current + 1;
    }
    focusable[next].focus();
  }

  function onEscape(e) {
    if (e.key !== 'Escape' || !activeModal) return;
    // Search in shadow root first (outer-close-button is sibling of content div),
    // fall back to the modal element itself.
    var searchRoot = activeShadowRoot || activeModal;
    var closeBtn = searchRoot.querySelector(
      '[part="outside-close-button"], [part="close-button"], [aria-label="Close modal"]'
    );
    if (closeBtn) closeBtn.click();
  }

  // Safety net: focusin also bubbles out of shadow DOM via composedPath
  function onFocusIn(e) {
    if (!activeModal) return;
    var path = e.composedPath ? e.composedPath() : [];
    if (path.indexOf(activeModal) !== -1) return;
    var focusable = getFocusable(activeModal);
    (focusable[0] || activeModal).focus();
  }

  function isVisible(el) {
    return el.getAttribute('aria-hidden') !== 'true' &&
           !el.hasAttribute('hidden') &&
           (el.offsetWidth > 0 || el.offsetHeight > 0);
  }

  // ─── Full mode: focus trap + return ────────────────────────────────────────
  // Used for shadow-DOM inner dialogs and pure light-DOM dialogs.

  function activate(sr, modal) {
    if (activeModal === modal) return;
    activeModal = modal;
    activeShadowRoot = sr;
    // preclickFocused is set at pointerdown, BEFORE the web component moves focus.
    previouslyFocused = preclickFocused || document.activeElement || document.body;

    requestAnimationFrame(function () {
      var focusable = getFocusable(modal);
      (focusable[0] || modal).focus();
    });
  }

  function deactivate() {
    if (!activeModal) return;
    activeModal = null;
    activeShadowRoot = null;
    var target = previouslyFocused || document.body;
    previouslyFocused = null;
    preclickFocused = null;
    requestAnimationFrame(function () {
      if (target && target.focus) target.focus();
    });
  }

  // ─── Passive mode: focus return only ───────────────────────────────────────
  // Used for Horizon web-component wrappers (e.g. hdt-lazy-modal for quick view)
  // whose shadow DOM manages focus internally. We only restore focus on close.

  var passiveDialogs = typeof Map !== 'undefined' ? new Map() : null;

  function activatePassive(el) {
    if (!passiveDialogs || passiveDialogs.has(el)) return;
    passiveDialogs.set(el, preclickFocused || document.activeElement || document.body);
  }

  function deactivatePassive(el) {
    if (!passiveDialogs || !passiveDialogs.has(el)) return;
    var target = passiveDialogs.get(el) || document.body;
    passiveDialogs.delete(el);
    preclickFocused = null;
    requestAnimationFrame(function () {
      if (target && target.focus) target.focus();
    });
  }

  // ─── Shadow DOM support ────────────────────────────────────────────────────

  function checkShadow(host) {
    var sr = host.shadowRoot;
    if (!sr) return;
    // hdt-cart-drawer and hdt-drawer are excluded: their Tab handling is managed
    // by initCartDrawer() and the generic hdt-drawer handler below.
    var tag = host.tagName && host.tagName.toLowerCase();
    if (tag === 'hdt-cart-drawer' || tag === 'hdt-drawer') return;
    var modal = sr.querySelector('[role="dialog"][aria-modal="true"]:not(button)');
    if (!modal) {
      if (activeModal && !activeModal.isConnected) deactivate();
      return;
    }
    if (isVisible(modal)) {
      activate(sr, modal);
    } else if (activeModal === modal) {
      deactivate();
    }
  }

  function watchHost(host) {
    if (!host.shadowRoot) return;
    if (watchedHosts.indexOf(host) !== -1) return;
    watchedHosts.push(host);

    new MutationObserver(function () {
      checkShadow(host);
    }).observe(host.shadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'class', 'style', 'data-visible']
    });

    checkShadow(host);
  }

  function scanForHosts(root) {
    (root.querySelectorAll ? root.querySelectorAll('*') : [])
      .forEach(function (el) {
        if (el.shadowRoot) watchHost(el);
      });
  }

  // ─── Light DOM support ─────────────────────────────────────────────────────

  function checkLightDialog(el) {
    if (!el || el.getAttribute('role') !== 'dialog') return;
    // hdt-cart-drawer and hdt-drawer have dedicated Tab handlers — exclude here.
    var tag = el.tagName && el.tagName.toLowerCase();
    if (tag === 'hdt-cart-drawer' || tag === 'hdt-drawer') return;

    if (el.shadowRoot) {
      // Web component with shadow DOM:
      // If its shadow already contains an inner [role="dialog"], checkShadow handles it.
      if (el.shadowRoot.querySelector('[role="dialog"][aria-modal="true"]:not(button)')) return;
      // Otherwise (e.g. hdt-lazy-modal), use passive mode — shadow manages focus trap,
      // we only restore focus to the trigger element on close.
      if (isVisible(el)) {
        activatePassive(el);
      } else {
        deactivatePassive(el);
      }
      return;
    }

    // Pure light-DOM dialog (e.g. custom_tax-free-popup): full mode.
    if (isVisible(el)) {
      activate(null, el);
    } else if (activeModal === el) {
      deactivate();
    }
  }

  function scanLightDialogs(root) {
    (root.querySelectorAll ? root.querySelectorAll('[role="dialog"][aria-modal="true"]') : [])
      .forEach(checkLightDialog);
  }

  // ─── Event listeners ────────────────────────────────────────────────────────

  // Capture phase — fires before browser moves focus and before any other listener
  document.addEventListener('keydown', onTabCapture, true);
  document.addEventListener('keydown', onEscape);
  document.addEventListener('focusin', onFocusIn);

  // ─── Cart drawer: X button bridge + focus return ──────────────────────────
  // Horizon's Tab management works for light DOM content but skips the shadow
  // DOM close button. We only intercept Tab at the boundaries (first/last light
  // DOM element ↔ X button) and leave everything else to Horizon.

  function initCartDrawer() {
    var drawer = document.getElementById('CartDrawer');
    if (!drawer) return;

    var cartTriggerEl = null;
    var cartWasVisible = false;

    function getCloseBtn() {
      var sr = drawer.shadowRoot;
      if (!sr) return null;
      // Direct child of cart shadow DOM
      var btn = sr.querySelector('[part="close-button"]');
      if (btn) return btn;
      // wrapp-close-btn may have its own shadow DOM — pierce one level deeper
      var wrapper = sr.querySelector('[part="close-button-wrapper"]');
      if (wrapper && wrapper.shadowRoot) {
        btn = wrapper.shadowRoot.querySelector('button');
        if (btn) return btn;
      }
      // Last resort: any button inside the shadow header, excluding the overlay button
      var header = sr.querySelector('[part="header"]');
      return header ? header.querySelector('button') : null;
    }

    function getLightFocusable() {
      return Array.from(drawer.querySelectorAll(FOCUSABLE_SELECTORS)).filter(function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0;
      });
    }

    // Only intercept Tab at the boundaries — all other Tab presses go to Horizon.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !isVisible(drawer)) return;
      var closeBtn = getCloseBtn();
      if (!closeBtn) return;

      var path = e.composedPath ? e.composedPath() : [];
      var currentEl = path[0] || document.activeElement;
      var light = getLightFocusable();
      var first = light[0];
      var last  = light[light.length - 1];

      if (!e.shiftKey && currentEl === last) {
        // Tab past last light DOM element → X button
        e.preventDefault();
        closeBtn.focus();
        requestAnimationFrame(function () { closeBtn.focus(); });
      } else if (e.shiftKey && currentEl === first) {
        // Shift+Tab from first light DOM element → X button
        e.preventDefault();
        closeBtn.focus();
        requestAnimationFrame(function () { closeBtn.focus(); });
      } else if (currentEl === closeBtn) {
        // Tab/Shift+Tab from X button → wrap into light DOM
        e.preventDefault();
        var target = e.shiftKey ? last : first;
        if (target) target.focus();
        requestAnimationFrame(function () { if (target) target.focus(); });
      }
    }, true);

    // Focus return: save trigger when cart opens, restore when it closes.
    new MutationObserver(function () {
      var nowVisible = isVisible(drawer);
      if (!cartWasVisible && nowVisible) {
        cartTriggerEl = preclickFocused || document.activeElement || document.body;
      } else if (cartWasVisible && !nowVisible && cartTriggerEl) {
        var target = cartTriggerEl;
        cartTriggerEl = null;
        requestAnimationFrame(function () {
          if (target && target.focus) target.focus();
        });
      }
      cartWasVisible = nowVisible;
    }).observe(drawer, { attributes: true, attributeFilter: ['class', 'style', 'aria-hidden', 'hidden'] });
  }

  // ─── Generic hdt-drawer: Tab boundary bridge + focus return ──────────────────
  // Covers menu drawer, filter drawer, and any future hdt-drawer instances.
  // Horizon manages Tab through slotted light-DOM content natively; we only bridge
  // the gap between the last light-DOM item and the shadow-DOM close button.
  //
  // The Tab bridge is a SINGLE document listener that dynamically locates the
  // currently visible drawer — no static element references needed, so it works
  // even when drawers are added/replaced via AJAX.

  var visibleHdtDrawer = null;

  function getHdtDrawerCloseBtn(drawer) {
    var sr = drawer.shadowRoot;
    if (!sr) return null;
    var btn = sr.querySelector('[part="close-button"]');
    if (btn) return btn;
    var wrapper = sr.querySelector('[part="close-button-wrapper"]');
    if (wrapper && wrapper.shadowRoot) {
      btn = wrapper.shadowRoot.querySelector('button');
      if (btn) return btn;
    }
    var header = sr.querySelector('[part="header"]');
    if (header) {
      btn = header.querySelector('button');
      if (btn) return btn;
    }
    // Last resort: any non-overlay button in the shadow root
    var allBtns = sr.querySelectorAll('button:not([part="outside-close-button"])');
    return allBtns.length ? allBtns[0] : null;
  }

  function getHdtDrawerLightFocusable(drawer) {
    return Array.from(drawer.querySelectorAll(FOCUSABLE_SELECTORS)).filter(function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0;
    });
  }

  // Single Tab bridge for all hdt-drawer instances
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !visibleHdtDrawer) return;
    var drawer = visibleHdtDrawer;
    var closeBtn = getHdtDrawerCloseBtn(drawer);
    if (!closeBtn) return;

    var path = e.composedPath ? e.composedPath() : [];
    var currentEl = path[0] || document.activeElement;
    var light = getHdtDrawerLightFocusable(drawer);
    var first = light[0];
    var last = light[light.length - 1];

    if (!e.shiftKey && currentEl === last) {
      e.preventDefault();
      closeBtn.focus();
      requestAnimationFrame(function () { closeBtn.focus(); });
    } else if (e.shiftKey && currentEl === first) {
      e.preventDefault();
      closeBtn.focus();
      requestAnimationFrame(function () { closeBtn.focus(); });
    } else if (currentEl === closeBtn) {
      e.preventDefault();
      var target = e.shiftKey ? last : first;
      if (target) target.focus();
      requestAnimationFrame(function () { if (target) target.focus(); });
    }
  }, true);

  // Attach focus/blur listeners directly on the close button so the outline
  // uses inline style with !important — overrides any theme "outline: none !important".
  function attachCloseBtnFocusIndicator(btn) {
    if (!btn || btn._moxieFocusAttached) return;
    btn._moxieFocusAttached = true;
    btn.addEventListener('focus', function () {
      btn.style.setProperty('outline', '2px solid currentColor', 'important');
      btn.style.setProperty('outline-offset', '3px', 'important');
      btn.style.setProperty('border-radius', '2px', 'important');
    });
    btn.addEventListener('blur', function () {
      btn.style.removeProperty('outline');
      btn.style.removeProperty('outline-offset');
      btn.style.removeProperty('border-radius');
    });
  }

  // Try to find the close button and attach the indicator. Returns true if successful.
  function tryAttachFocusIndicator(drawer) {
    var btn = getHdtDrawerCloseBtn(drawer);
    if (!btn) return false;
    attachCloseBtnFocusIndicator(btn);
    return true;
  }

  var watchedHdtDrawers = typeof Map !== 'undefined' ? new Map() : null;

  function watchHdtDrawer(drawer) {
    if (watchedHdtDrawers && watchedHdtDrawers.has(drawer)) return;
    tryAttachFocusIndicator(drawer);

    var state = { wasVisible: false, triggerEl: null };
    if (watchedHdtDrawers) watchedHdtDrawers.set(drawer, state);

    new MutationObserver(function () {
      var nowVisible = isVisible(drawer);
      if (!state.wasVisible && nowVisible) {
        tryAttachFocusIndicator(drawer); // retry if shadow root wasn't ready at init
        state.triggerEl = preclickFocused || document.activeElement || document.body;
        visibleHdtDrawer = drawer;
        requestAnimationFrame(function () {
          var light = getHdtDrawerLightFocusable(drawer);
          if (light[0]) light[0].focus();
        });
      } else if (state.wasVisible && !nowVisible) {
        if (visibleHdtDrawer === drawer) visibleHdtDrawer = null;
        if (state.triggerEl) {
          var target = state.triggerEl;
          state.triggerEl = null;
          requestAnimationFrame(function () {
            if (target && target.focus) target.focus();
          });
        }
      }
      state.wasVisible = nowVisible;
    }).observe(drawer, { attributes: true, attributeFilter: ['class', 'style', 'aria-hidden', 'hidden'] });
  }

  function initHdtDrawers(root) {
    (root || document).querySelectorAll('hdt-drawer').forEach(watchHdtDrawer);
  }

  // ─── Init ───────────────────────────────────────────────────────────────────

  function init() {
    scanForHosts(document);
    scanLightDialogs(document);
    initCartDrawer();
    initHdtDrawers(document);

    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        // Newly added nodes — scan for new shadow hosts, light dialogs, and hdt-drawers
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.shadowRoot) watchHost(node);
          scanForHosts(node);
          scanLightDialogs(node);
          var nodeTag = node.tagName && node.tagName.toLowerCase();
          if (nodeTag === 'hdt-drawer') watchHdtDrawer(node);
          if (node.querySelectorAll) initHdtDrawers(node);
        });

        // Attribute changes — re-check visibility of any light-DOM dialog
        if (mutation.type === 'attributes') {
          var el = mutation.target;
          if (el && el.getAttribute && el.getAttribute('role') === 'dialog') {
            checkLightDialog(el);
          }
        }
      });
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'aria-hidden', 'style', 'class']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
